import React, { FC } from "react";
import { Link } from "react-router-dom";

const TermsOfService: FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/logo_text.png" alt="Code Coast" className="h-8" />
            </Link>
            <Link
              to="/"
              className="text-[#14b7cd] uppercase text-sm px-6 py-2 border border-[#14b7cd] hover:bg-[#14b7cd] hover:text-white transition-colors duration-300"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Terms of Service
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                These Terms of Service ("Terms") govern your use of the Code Coast platform 
                and services. By accessing or using our services, you agree to be bound by 
                these Terms. Please read them carefully.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Effective Date:</strong> These Terms are effective as of {new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })} and will remain in effect until modified or terminated.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-8">
                By accessing or using the Code Coast platform, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms and our Privacy Policy. If you 
                do not agree to these Terms, you may not use our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Code Coast provides an online educational platform that offers:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Interactive programming courses and tutorials</li>
                <li>Live coding sessions and workshops</li>
                <li>Startup school programs and business training</li>
                <li>Community forums and discussion boards</li>
                <li>Progress tracking and certification programs</li>
                <li>Payment processing for course enrollments</li>
                <li>Customer support and technical assistance</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To access certain features of our platform, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information as necessary</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Eligibility</h3>
              <p className="text-gray-700 mb-8">
                You must be at least 13 years old to create an account. Users under 18 must have 
                parental consent. We reserve the right to refuse service to anyone at any time.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Permitted Uses</h3>
              <p className="text-gray-700 mb-4">
                You may use our platform for lawful educational purposes only. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Use the platform in accordance with these Terms</li>
                <li>Respect intellectual property rights</li>
                <li>Maintain a respectful learning environment</li>
                <li>Follow community guidelines and policies</li>
                <li>Report violations of these Terms</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Prohibited Activities</h3>
              <p className="text-gray-700 mb-4">
                You agree not to engage in any of the following activities:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with the proper functioning of the platform</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Share inappropriate, offensive, or illegal content</li>
                <li>Use automated tools to access the platform without permission</li>
                <li>Resell or redistribute our content without authorization</li>
                <li>Create multiple accounts to circumvent restrictions</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Pricing and Billing</h3>
              <p className="text-gray-700 mb-4">
                Course fees and subscription prices are displayed on our platform. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Pay all fees associated with your enrollment</li>
                <li>Provide accurate payment information</li>
                <li>Authorize us to charge your payment method</li>
                <li>Notify us of any changes to your payment information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Refunds</h3>
              <p className="text-gray-700 mb-8">
                Refund policies vary by course and program. Generally, refunds are available within 
                30 days of enrollment if you have not accessed more than 20% of the course content. 
                Contact our support team for specific refund requests.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Our Content</h3>
              <p className="text-gray-700 mb-4">
                All content on the Code Coast platform, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Course materials, videos, and tutorials</li>
                <li>Software, code examples, and documentation</li>
                <li>Graphics, logos, and branding materials</li>
                <li>Platform design and user interface</li>
                <li>Assessment tools and certification programs</li>
              </ul>
              <p className="text-gray-700 mb-6">
                Are protected by copyright, trademark, and other intellectual property laws. 
                You may not copy, distribute, or create derivative works without our written permission.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 User Content</h3>
              <p className="text-gray-700 mb-8">
                You retain ownership of content you create and submit to our platform. However, 
                you grant us a license to use, display, and distribute your content in connection 
                with our services. You are responsible for ensuring you have the right to share 
                any content you submit.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
              <p className="text-gray-700 mb-8">
                Your privacy is important to us. Our collection and use of personal information 
                is governed by our Privacy Policy, which is incorporated into these Terms by reference. 
                By using our services, you consent to the collection and use of information as 
                described in our Privacy Policy.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitations</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Service Availability</h3>
              <p className="text-gray-700 mb-6">
                We strive to maintain high service availability, but we cannot guarantee uninterrupted 
                access. We may experience downtime for maintenance, updates, or technical issues.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Educational Outcomes</h3>
              <p className="text-gray-700 mb-6">
                While we provide quality educational content, we cannot guarantee specific learning 
                outcomes, job placements, or career advancement. Your success depends on your effort, 
                dedication, and individual circumstances.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 Limitation of Liability</h3>
              <p className="text-gray-700 mb-8">
                To the maximum extent permitted by law, Code Coast shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages, including but 
                not limited to loss of profits, data, or use, arising from your use of our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Termination by You</h3>
              <p className="text-gray-700 mb-6">
                You may terminate your account at any time by contacting our support team or 
                using the account deletion features in your profile settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Termination by Us</h3>
              <p className="text-gray-700 mb-8">
                We may suspend or terminate your account immediately if you violate these Terms, 
                engage in fraudulent activity, or for any other reason at our sole discretion.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications to Terms</h2>
              <p className="text-gray-700 mb-8">
                We reserve the right to modify these Terms at any time. We will notify users of 
                material changes through email or platform notifications. Your continued use of 
                our services after such modifications constitutes acceptance of the updated Terms.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law and Disputes</h2>
              <p className="text-gray-700 mb-8">
                These Terms are governed by the laws of Ghana. Any disputes arising from these 
                Terms or your use of our services will be resolved through binding arbitration 
                or in the courts of Ghana. We encourage users to contact us first to resolve 
                any issues amicably.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Miscellaneous</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.1 Severability</h3>
              <p className="text-gray-700 mb-6">
                If any provision of these Terms is found to be unenforceable, the remaining 
                provisions will remain in full force and effect.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.2 Entire Agreement</h3>
              <p className="text-gray-700 mb-6">
                These Terms, together with our Privacy Policy, constitute the entire agreement 
                between you and Code Coast regarding your use of our services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.3 Waiver</h3>
              <p className="text-gray-700 mb-8">
                Our failure to enforce any provision of these Terms does not constitute a 
                waiver of that provision or any other provision.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> <a href="mailto:legal@codecoast.org" className="text-[#14b7cd] hover:underline">legal@codecoast.org</a></li>
                  <li><strong>Support:</strong> <a href="mailto:support@codecoast.org" className="text-[#14b7cd] hover:underline">support@codecoast.org</a></li>
                  <li><strong>Phone:</strong> +233 24 086 5997</li>
                  <li><strong>Address:</strong> Ekumfi Essuehyia, Ekumfiman, Ghana</li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      <strong>Questions?</strong> We're here to help. If you have any questions about these Terms 
                      or need clarification on any provision, please don't hesitate to contact our legal team.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center pt-8 border-t border-gray-200">
                <p className="text-gray-500 text-sm">
                  Last updated: {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} Code Coast. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
